import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { montantsRegle, fmtArgent, LIBELLES_FREQUENCES } from '@/lib/lmdsDeductions';
import { libelleSemaine, nbSemaines } from '@/lib/lmdsSemaines';

// 🧾 Phase 2 : formulaire d'une règle de déduction avec aperçu avant taxes / TPS / TVQ / total en direct.
// Numérotation dimanche–samedi (`lib/lmdsSemaines`, identique au backend) — jamais la semaine ISO du navigateur.
const SemaineInputs = ({ prefix, valeur, onChange, annee }) => {
  const a = valeur?.annee ?? annee;
  const max = nbSemaines(a);
  return (
    <div>
      <div className="flex gap-2">
        <Input type="number" min={2020} max={2100} value={a} data-testid={`${prefix}-annee`}
          onChange={(e) => onChange({ annee: parseInt(e.target.value) || annee, semaine: valeur?.semaine ?? 1 })} className="w-24" />
        <Input type="number" min={1} max={max} placeholder="Semaine" value={valeur?.semaine ?? ''} data-testid={`${prefix}-semaine`}
          onChange={(e) => onChange({ annee: a, semaine: Math.min(max, parseInt(e.target.value) || 1) })} className="w-24" />
      </div>
      {valeur?.semaine > 0 && <p className="text-xs text-slate-500 mt-1" data-testid={`${prefix}-dates`}>Semaine {valeur.semaine} : {libelleSemaine(a, valeur.semaine)} (dimanche → samedi)</p>}
    </div>
  );
};

export function FormulaireRegleDeduction({ installateurs, installateurId, onSoumettre, enCours }) {
  const annee = new Date().getFullYear();
  const [f, setF] = useState({ installateur_id: installateurId || '', description: '', montant_saisi: '', mode_taxes: 'taxes_incluses',
    frequence: 'hebdomadaire', semaine_cible: null, semaine_debut: null, semaine_fin: null });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const apercu = useMemo(() => montantsRegle(f.montant_saisi, f.mode_taxes), [f.montant_saisi, f.mode_taxes]);
  const valide = f.installateur_id && f.description.length >= 2 && parseFloat(f.montant_saisi) > 0
    && (f.frequence === 'unique' ? f.semaine_cible?.semaine : f.semaine_debut?.semaine);

  const soumettre = (e) => {
    e.preventDefault();
    const corps = { installateur_id: f.installateur_id, description: f.description, montant_saisi: parseFloat(f.montant_saisi),
      mode_taxes: f.mode_taxes, frequence: f.frequence };
    if (f.frequence === 'unique') corps.semaine_cible = f.semaine_cible;
    else { corps.semaine_debut = f.semaine_debut; if (f.semaine_fin?.semaine) corps.semaine_fin = f.semaine_fin; }
    onSoumettre(corps).then((ok) => ok && setF((p) => ({ ...p, description: '', montant_saisi: '' })));
  };

  return (
    <form onSubmit={soumettre} className="grid gap-3 md:grid-cols-2" data-testid="formulaire-regle-deduction">
      <div>
        <Label>Installateur</Label>
        <select className="w-full border rounded-md h-9 px-2 text-sm bg-white" value={f.installateur_id} data-testid="regle-installateur"
          onChange={(e) => set('installateur_id', e.target.value)}>
          <option value="">— Choisir —</option>
          {installateurs.map((i) => <option key={i.id} value={i.id}>{i.nom}</option>)}
        </select>
      </div>
      <div>
        <Label>Description</Label>
        <Input value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="Ex. Utilisation/location du véhicule" data-testid="regle-description" />
      </div>
      <div>
        <Label>Montant</Label>
        <Input type="number" step="0.01" min="0" value={f.montant_saisi} onChange={(e) => set('montant_saisi', e.target.value)} data-testid="regle-montant" />
      </div>
      <div>
        <Label>Taxes</Label>
        <select className="w-full border rounded-md h-9 px-2 text-sm bg-white" value={f.mode_taxes} data-testid="regle-mode-taxes" onChange={(e) => set('mode_taxes', e.target.value)}>
          <option value="taxes_incluses">Montant taxes incluses</option>
          <option value="avant_taxes">Montant avant taxes</option>
        </select>
      </div>
      <div className="md:col-span-2 grid grid-cols-4 gap-2 text-xs bg-slate-50 border rounded-md p-2" data-testid="regle-apercu-taxes">
        <div>Avant taxes<br /><b data-testid="apercu-avant-taxes">{fmtArgent(apercu.montant_avant_taxes)}</b></div>
        <div>TPS<br /><b data-testid="apercu-tps">{fmtArgent(apercu.tps)}</b></div>
        <div>TVQ<br /><b data-testid="apercu-tvq">{fmtArgent(apercu.tvq)}</b></div>
        <div>Total<br /><b data-testid="apercu-total">{fmtArgent(apercu.total)}</b></div>
      </div>
      <div>
        <Label>Fréquence</Label>
        <select className="w-full border rounded-md h-9 px-2 text-sm bg-white" value={f.frequence} data-testid="regle-frequence" onChange={(e) => set('frequence', e.target.value)}>
          {Object.entries(LIBELLES_FREQUENCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {f.frequence === 'unique' ? (
        <div><Label>Semaine cible (année / semaine)</Label><SemaineInputs prefix="regle-cible" valeur={f.semaine_cible} onChange={(v) => set('semaine_cible', v)} annee={annee} /></div>
      ) : (
        <>
          <div><Label>Semaine de début</Label><SemaineInputs prefix="regle-debut" valeur={f.semaine_debut} onChange={(v) => set('semaine_debut', v)} annee={annee} /></div>
          <div><Label>Semaine de fin (facultatif)</Label><SemaineInputs prefix="regle-fin" valeur={f.semaine_fin} onChange={(v) => set('semaine_fin', v)} annee={annee} /></div>
        </>
      )}
      <div className="md:col-span-2">
        <Button type="submit" disabled={!valide || enCours} data-testid="regle-soumettre">Créer la déduction</Button>
      </div>
    </form>
  );
}
